import type { Category } from "@/components/estimation-details/Rooms/Lines";
import type { IQuantityFormula } from "@/components/estimation-details/Rooms/Lines/util/engine-shared-utils";
import EngineSharedUtils from "@/components/estimation-details/Rooms/Lines/util/engine-shared-utils";
import { EstimateType } from "@/enums/estimate-type";
import type { DataForUI, EstimateDetail, LineItem, MappedTuple, Page, PageMetadata, Tuple } from "@/interfaces/estimate-detail";
import type { AddNoteBody, ClusterToRoomMapping, Estimation, MeasurementData } from "@/interfaces/estimation.interface";
import type { LineItemElementData, LineItemSearchData } from "@/interfaces/line-item-elements-response";
import type {
  CreateLineItemParams,
  CreateOrUpdateItemLinePayload,
  CreateOrUpdateItemLineResponse,
  DeleteLineItemParams,
  LineItemData,
  UpdateLineItemParams,
} from "@/interfaces/payloads/line-item";
import type { IXactimateUserRowPayload } from "@/interfaces/payloads/xactimate";
import { default as EstimateProvider } from "@/providers/estimate.provider";
import { estimationActions } from "@/redux/actions/estimation.actions";
import { estimationSelector } from "@/redux/selectors/estimation.selector";

import { store, useDispatch, useSelector } from "@/redux/store";
import { capitalizeWords } from "@rebuild/shared";
import type { ScopeNoteProcessingStatus } from "@rebuild/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { clearRequestsCache } from "@/providers/http.provider";
import { isItemArchived } from "@rebuild/shared";
import { queryClient } from "@/lib/query-client";
import { useRateCardUnitByTemplateId } from "@/hooks/queries/rate-cards";
import { ESTIMATE_KEYS } from "./queries/estimates/queryKeys";
import { useEstimateJobStatus } from "./queries/jobs/useEstimateJobStatus";
import { useUpdateLineItemMutation } from "./mutation/estimates/useUpdateLineItemMutation";
import { applyOptimisticUpdateToEstimationDetail } from "@/lib/estimate-line-items";
import { applyUpdateToState, buildLineItemUpdateBatch, getDetailFromPossiblyMergedPage } from "./useEstimateDetail.helpers";
import { syncEstimateDetailFromServerToRedux } from "./estimate/syncEstimateDetailFromServer";

interface EnhancedRoomCacheEntry {
  source: EstimateDetail;
  mergedSources: EstimateDetail[];
  output: EstimateDetail;
}

// How often to refetch estimate metadata while add/update-documentation is reprocessing, to
// observe scopeNoteProcessingStatus flipping to "complete"/"error".
const SCOPE_NOTE_REPROCESS_POLL_MS = 3000;

// Pure helper — given a state snapshot and delete params, returns the next state and the API
// payload immutably. Hoisted out of the hook so it doesn't churn callback identities.
const _applyDeleteToState = (
  currentState: EstimateDetail[],
  deleteParams: {
    pageId: string;
    tupleId: string;
    uniqueId: string;
    category?: Category;
  },
): {
  modifiedState: EstimateDetail[] | null;
  apiData: {
    pageIdForApi: string;
    tupleData: LineItemData;
    tupleId: string;
  } | null;
} => {
  const { pageId, tupleId, uniqueId, category } = deleteParams;

  const { currentPageIndex, tuples, tupleIndex } = getDetailFromPossiblyMergedPage(currentState, pageId, tupleId);
  const room = currentState[currentPageIndex];

  if (!room || !tuples[tupleIndex]) {
    console.error("[_applyDeleteToState] Delete failed: Page or tuple not found");
    return { modifiedState: null, apiData: null };
  }

  const pageIdForApi = room.dataForUI.metadata.pageId;
  const sourceTuple = tuples[tupleIndex];
  const lineItemIndex = sourceTuple.lineItems.findIndex((li) => li.uniqueId === uniqueId);

  if (lineItemIndex === -1) {
    console.error("[_applyDeleteToState] Delete failed: Line item not found");
    return { modifiedState: null, apiData: null };
  }

  const archiveWholeTuple = !!category && !sourceTuple.lineItems[lineItemIndex].isComplement;
  const newLineItems = sourceTuple.lineItems.map((li, i) => {
    if (archiveWholeTuple || i === lineItemIndex) return { ...li, archived: true };
    return li;
  });
  const newTuple: Tuple = {
    ...sourceTuple,
    ...(archiveWholeTuple ? { archived: true } : {}),
    lineItems: newLineItems,
  };
  const newTuples = tuples.map((t, i) => (i === tupleIndex ? newTuple : t));
  const newRoom: EstimateDetail = {
    ...room,
    dataForUI: {
      ...room.dataForUI,
      page: {
        ...room.dataForUI.page,
        tuplesOutput: newTuples,
      },
    },
  };
  const modifiedState = currentState.map((r, i) => (i === currentPageIndex ? newRoom : r));

  return {
    modifiedState,
    apiData: {
      pageIdForApi,
      tupleData: newTuple as unknown as LineItemData,
      tupleId,
    },
  };
};

const _applyCreateToState = (
  currentState: EstimateDetail[],
  createParams: CreateLineItemParams,
): {
  modifiedState: EstimateDetail[] | null;
  apiData: { pageId: string; lineItemData: LineItemData } | null;
} => {
  const { pageId, item, lineItemId, quantity, orderIndex, measurement, formula } = createParams;

  const currentPageIndex = currentState.findIndex((detail) => detail.dataForUI.metadata.pageId === pageId);

  if (currentPageIndex === -1) {
    console.error("[_applyCreateToState] Create failed: Page not found");
    return { modifiedState: null, apiData: null };
  }

  const room = currentState[currentPageIndex];
  const existingTuples = room.dataForUI.page.tuplesOutput;

  const newLineItemData: LineItemData = {
    tupleId: `user_${uuidv4()}`,
    tupleRegion: { height: 0, width: 0, left: 0, top: 0 },
    lineItems: [
      {
        uniqueId: uuidv4(),
        leftMapping: [],
        rightMapping: [],
        mappingExtraData: [],
        qnm: [{ quantity, measurement: measurement }],
        lineItem: item,
        isComplement: false,
        lineItemId: lineItemId,
        deleted: false,
        addedByTPARule: false,
        confidence: 1,
        confirmed: true,
        formula: formula ?? undefined,
        isMutuallyExcluded: false,
        isExcludedBy: [],
        orderIndex,
      },
    ],
    isLikely: false,
    isComplementOf: "",
    originalTupleIndex: orderIndex || existingTuples.length || 0,
    deleted: false,
  };

  const newRoom: EstimateDetail = {
    ...room,
    dataForUI: {
      ...room.dataForUI,
      page: {
        ...room.dataForUI.page,
        tuplesOutput: [...existingTuples, newLineItemData as unknown as Tuple],
      },
    },
  };
  const modifiedState = currentState.map((r, i) => (i === currentPageIndex ? newRoom : r));

  return {
    modifiedState,
    apiData: {
      pageId: pageId,
      lineItemData: newLineItemData,
    },
  };
};

export const useEstimateDetail = (estimateId: string, options: { pollJobs?: boolean } = {}) => {
  const dispatch = useDispatch();
  const [showAlert, setShowAlert] = useState<{
    title?: string;
    errors: string[];
    isValid: boolean;
  }>({
    isValid: true,
    errors: [],
  });

  const files = useSelector(estimationSelector.files);
  const notes = useSelector(estimationSelector.notes);
  const links = useSelector(estimationSelector.links);
  const isLoading = useSelector(estimationSelector.isLoading);
  const isPdfLoading = useSelector(estimationSelector.isPdfLoading);
  const isUpdateScopeNoteLoading = useSelector(estimationSelector.isUpdateScopeNoteLoading);
  const currentRoom = useSelector(estimationSelector.currentRoom);
  const estimation = useSelector(estimationSelector.estimation);
  const fixedMapping = useSelector(estimationSelector.fixedMapping);
  const lineItemDetail = useSelector(estimationSelector.lineItemDetail);
  const missingMappings = useSelector(estimationSelector.missingMappings);
  const estimationDetail = useSelector(estimationSelector.estimationDetail);
  const fullPageImagePath = useSelector(estimationSelector.fullPageImagePath);
  const rightSidebarCurrentTab = useSelector(estimationSelector.rightSidebarCurrentTab);
  const showMissingMappingModal = useSelector(estimationSelector.showMissingMappingModal);
  const { allCompleted, hasFailedJobs, hasActiveJobs } = useEstimateJobStatus(estimateId, { enabled: options.pollJobs ?? false });
  const lineItemXactimateMapping = useSelector(estimationSelector.lineItemXactimateMapping);
  // For T&M estimates, unit metadata comes from the selected rate card's items
  // rather than xactimate mappings. This map is empty for non-T&M estimates.
  const { unitByTemplateId: rateCardUnitByTemplateId } = useRateCardUnitByTemplateId(
    estimation?.estimateType === EstimateType.TIME_AND_MATERIAL ? estimation?.rateCardId : undefined,
  );
  const exportedToXactimate = useSelector(estimationSelector.exportedToXactimate);
  const isUpdateLineItemLoading = useSelector(estimationSelector.isUpdateLineItemLoading);
  const knownLineItemComponentKeys = useSelector(estimationSelector.knownLineItemComponentKeys);

  const { mutateAsync: updateLineItemMutateAsync } = useUpdateLineItemMutation();

  const hasPdfExport = useMemo(
    () => !!estimation?.exports?.some((exp) => exp.type === "PDF" && !!exp.pdfExportStoragePath),
    [estimation?.exports],
  );

  const resetEstimationDetailData = useCallback(() => {
    dispatch(
      estimationActions.setEstimationState({
        isLoading: false,
        isPdfLoading: false,
        isUpdateScopeNoteLoading: false,
        isUpdateLineItemLoading: false,
        files: [],
        notes: [],
        links: [],
        estimationDetail: undefined,
        uploadedFilesPropertiesCreated: [],
        estimation: undefined,
        rightSidebarCurrentTab: {
          value: "Details",
          id: "Details",
        },
        lineItemDetail: undefined,
        currentRoom: undefined,
        fullPageImagePath: undefined,
      }),
    );
  }, [dispatch]);

  // only fetch estimate if it's undefined in the redux state
  const [shouldFetchEstimate, setShouldFetchEstimate] = useState(estimation === undefined);
  // Terminal state: the estimate was deleted (server answered 404). Once set, the refetch
  // effects below stop firing so we don't tight-loop on a missing estimate. Reset when the
  // estimateId changes so navigating to a different estimate fetches fresh.
  const [estimateNotFound, setEstimateNotFound] = useState(false);

  useEffect(() => {
    setEstimateNotFound(false);
  }, [estimateId]);

  useEffect(() => {
    const getEstimateMetadata = async () => {
      if (estimateId === undefined) {
        console.error(`undefined estimateId, won't fetch estimate`);
        return;
      }

      try {
        const estimates = await EstimateProvider.getEstimates(estimateId);
        const estimation = estimates?.[0];

        if (estimation === undefined) {
          console.error(`failed to load estimate for id ${estimateId}`);
          return;
        }

        dispatch(
          estimationActions.setEstimationState({
            estimation,
            links: estimation.links ?? [],
            notes: estimation.notes ?? [],
            files: estimation.files ?? [],
          }),
        );
      } catch (err) {
        // 404 => the estimate is gone (deleted). Land on the terminal not-found state so the
        // refetch effects stop instead of looping. Any other error is transient — log it.
        if ((err as { error?: { status?: number } })?.error?.status === 404) {
          setEstimateNotFound(true);
          return;
        }
        console.error(`failed to load estimate for id ${estimateId}`, err);
      }
    };

    if (shouldFetchEstimate) {
      if (!estimateNotFound) {
        getEstimateMetadata();
      }
      setShouldFetchEstimate(false);
    }
  }, [shouldFetchEstimate, dispatch, estimateId, estimateNotFound]);

  const updateEstimate = useCallback(
    async (estimateUpdates: Partial<Estimation>) => {
      const { success } = await EstimateProvider.updateEstimateField({
        estimaticsData: {
          estimateId,
          ...estimateUpdates,
        },
      });

      if (!success) {
        throw new Error("Failed to update estimate");
      }

      setShouldFetchEstimate(true);
      // cheap way to trigger refetching /get_final_estimates
      await setCurrentViewEstimateId(estimateId, undefined, true);
    },
    [estimateId],
  );

  const updateOrCreateLineItem = useCallback(
    async (updates: CreateOrUpdateItemLinePayload): Promise<CreateOrUpdateItemLineResponse> => {
      if (import.meta.env.VITE_BLOCK_CONCURRENT_LINE_ITEM_UPDATES === "true" && store.getState().estimation.isUpdateLineItemLoading) {
        throw new Error(`Currently unable to update or create line item, please try again`);
      }

      // Rollback target: the captured array reference. Since the optimistic update below produces
      // a new top-level array (untouched rooms keep their refs), the original is never mutated and
      // can be reused as-is on error — no deep clone needed.
      const currentEstimationDetail = store.getState().estimation.estimationDetail;
      const originalState = currentEstimationDetail;
      try {
        if (currentEstimationDetail !== undefined) {
          const optimisticState = applyOptimisticUpdateToEstimationDetail(currentEstimationDetail, {
            pageId: updates.pageId,
            lineItemData: updates.lineItemData,
            tupleId: updates.lineItemId,
          });

          dispatch(
            estimationActions.setEstimationState({
              estimationDetail: optimisticState,
            }),
          );
        }

        dispatch(estimationActions.setEstimationState({ isUpdateLineItemLoading: true }));

        // Delegate to the mutation hook so the React Query cache (ESTIMATE_KEYS.detail)
        // stays in sync. Other consumers — e.g. useBatchUpdateLineItemsMutation — read
        // their source-of-truth state from that cache, so without this any mid-flight
        // edits would be lost when a follow-up batch operation runs.
        const response = await updateLineItemMutateAsync({
          estimateId: updates.estimateId,
          pageId: updates.pageId,
          lineItemData: updates.lineItemData,
          tupleId: updates.lineItemId,
          source: updates.source,
          metadata: updates.metadata,
        });

        if (!response.success) {
          throw new Error(`Unable to update line item`);
        }

        // if there is a line item currently selected, update it with api response data
        // Read fresh from store to avoid stale closure value after unSelectLineItem()
        const currentLineItemDetail = store.getState().estimation.lineItemDetail;
        if (currentLineItemDetail) {
          const pageId = currentLineItemDetail.tuple.pageMetadata?.pageId;
          const tupleId = currentLineItemDetail.tuple.tupleId;
          const uniqueId = currentLineItemDetail.lineItem.uniqueId;

          const updatedRoom = response.data.find((room) => room.dataForUI.metadata.pageId === pageId);
          const updatedTuple = updatedRoom
            ? Object.values(updatedRoom.dataForUI.page.tuplesOutput).find((tuple) => tuple.tupleId === tupleId)
            : undefined;
          const updatedLineItem = updatedTuple?.lineItems.find((lineItem) => lineItem.uniqueId === uniqueId);

          if (pageId && updatedLineItem && updatedTuple) {
            dispatch(
              estimationActions.setEstimationState({
                lineItemDetail: {
                  tuple: updatedTuple,
                  lineItem: updatedLineItem,
                  pageId: pageId,
                },
              }),
            );
          }
        }

        dispatch(
          estimationActions.setEstimationState({
            estimationDetail: response.data,
          }),
        );

        return response;
      } catch (e) {
        dispatch(
          estimationActions.setEstimationState({
            estimationDetail: originalState,
          }),
        );
        throw new Error(`Unable to update line item: ${e}`);
      } finally {
        dispatch(estimationActions.setEstimationState({ isUpdateLineItemLoading: false }));
      }
    },
    [dispatch, updateLineItemMutateAsync],
  );

  const searchEstimateDetail = useCallback(
    async (estimateId: string, options?: { silent?: boolean }) => {
      return syncEstimateDetailFromServerToRedux(dispatch, estimateId, options);
    },
    [dispatch],
  );

  const addKnownLineItemKey = useCallback(
    (key: string) => {
      dispatch(
        estimationActions.setEstimationState({
          knownLineItemComponentKeys: [...knownLineItemComponentKeys, key],
        }),
      );
    },
    [dispatch, knownLineItemComponentKeys],
  );

  const selectLineItem = useCallback(
    (uniqueId: string, category?: Category) => {
      const currentLineItemDetail: typeof lineItemDetail = store.getState().estimation.lineItemDetail;
      const currentEstimationDetail: EstimateDetail[] | undefined = store.getState().estimation.estimationDetail;

      // Check if already selected
      if (currentLineItemDetail?.lineItem.uniqueId === uniqueId) {
        return;
      }

      // Search through all pages and tuples to find the line item by uniqueId
      let foundPage: EstimateDetail | undefined;
      let foundTuple: Tuple | undefined;
      let foundLineItem: LineItem | undefined;

      for (const page of currentEstimationDetail || []) {
        for (const tuple of page.dataForUI.page.tuplesOutput || []) {
          const lineItem = tuple.lineItems.find((li) => li.uniqueId === uniqueId);
          if (lineItem) {
            foundPage = page;
            foundTuple = tuple;
            foundLineItem = lineItem;
            break;
          }
        }
        if (foundLineItem) break;
      }

      if (import.meta.env.VITE_LOG_SELECTED_LINE_ITEM === "true") {
        console.log({ lineItem: foundLineItem, tuple: foundTuple });
      }

      if (foundTuple && foundPage && foundLineItem) {
        const pageId = foundPage.dataForUI.metadata.pageId;
        // The general room doesn't have an image path, so the path is saved on each tuple
        const fullPageImagePath = foundPage.dataForUI.metadata.fullPageImagePath || foundTuple.pageMetadata?.fullPageImagePath;
        dispatch(
          estimationActions.setEstimationState({
            fullPageImagePath,
            rightSidebarCurrentTab: {
              value: "Item",
              customValue: foundTuple.tupleId,
              id: foundTuple.tupleId,
              category,
              lineItem: foundLineItem,
            },
            lineItemDetail: {
              tuple: foundTuple,
              lineItem: foundLineItem,
              pageId: pageId,
            },
            currentRoom: {
              value: capitalizeWords(foundPage?.dataForUI?.roomName) ?? "",
              id: foundPage?.dataForUI?.metadata.pageId ?? "",
            },
          }),
        );
      }
    },
    [dispatch],
  );

  const unSelectLineItem = useCallback(() => {
    dispatch(
      estimationActions.setEstimationState({
        rightSidebarCurrentTab: { value: "Details", id: "Details" },
        lineItemDetail: undefined,
      }),
    );
  }, [dispatch]);

  const deleteLineItem = useCallback(
    async (tupleId: string, pageId: string, uniqueId: string, category?: Category) => {
      // Get the latest state from the store
      const latestState = store.getState().estimation.estimationDetail;
      if (!latestState) return;

      if (!category) {
        const confirmDeletion = confirm("Are you sure you want to delete this line item?");
        if (!confirmDeletion) return;
      }

      // 1. Apply deletion to state copy
      const { apiData } = _applyDeleteToState(latestState, {
        pageId,
        tupleId,
        uniqueId,
        category,
      });

      if (!apiData) {
        console.error("Failed to prepare state for deletion.");
        return; // Exit if state modification failed
      }

      try {
        // 3. Perform API call
        const response = await updateOrCreateLineItem({
          lineItemId: apiData.tupleId,
          estimateId: estimateId,
          pageId: apiData.pageIdForApi, // Use resolved page ID
          lineItemData: [apiData.tupleData],
          source: "",
          metadata: {},
        });

        if (!response.success) {
          throw new Error("Error deleting line item");
        }

        unSelectLineItem();
      } catch (err) {
        toast.error("Error deleting line item");
        console.error("Error deleting line item ", err);
      }
    },
    [estimateId, updateOrCreateLineItem, unSelectLineItem],
  );

  const createLineItem = useCallback(
    async (
      pageId: string,
      item: string,
      lineItemId: string,
      quantity?: number,
      measurement?: string,
      formula?: IQuantityFormula | null,
      orderIndex?: number,
    ) => {
      // Get the latest state from the store
      const latestState = store.getState().estimation.estimationDetail;
      if (!latestState) return;

      // 1. Apply creation to state copy
      const { apiData } = _applyCreateToState(latestState, {
        pageId,
        item,
        lineItemId,
        quantity,
        measurement,
        formula,
        orderIndex,
      });

      if (!apiData) {
        console.error("Failed to prepare state for creation.");
        return;
      }

      // 2. Perform API call
      try {
        const response = await updateOrCreateLineItem({
          lineItemId: apiData.lineItemData.tupleId, // Use the newly generated tupleId
          estimateId: estimateId,
          pageId: apiData.pageId,
          lineItemData: [apiData.lineItemData],
          source: "",
          metadata: {},
        });

        if (!response.success) {
          toast.error("Error creating line item");
          // Potentially revert optimistic update or notify user
          return;
        }

        // 4. Update Xactimate mapping for the new line item
        dispatch(estimationActions.updateXactimateMappingForLineItem(item, lineItemId));
      } catch (err) {
        console.error("Error creating line item", err);
      }
    },
    [dispatch, estimateId, updateOrCreateLineItem],
  );

  const updateMetadataForExpectedDoc = useCallback(
    async (metadataUpdates: Partial<PageMetadata>) => {
      const currentEstimationDetail: EstimateDetail[] | undefined = store.getState().estimation.estimationDetail;
      if (!currentEstimationDetail || metadataUpdates.pageId === undefined || metadataUpdates.pageId === null) {
        return false;
      }

      const roomIndexToUpdate = currentEstimationDetail.findIndex((e) => e.dataForUI.metadata.pageId === metadataUpdates.pageId);
      const roomToUpdate = currentEstimationDetail[roomIndexToUpdate];

      if (!roomToUpdate) {
        return false;
      }

      const updatedMetadata: PageMetadata = {
        ...roomToUpdate.dataForUI.metadata,
        ...metadataUpdates,
      };

      const response = await EstimateProvider.updateMetadataFinalEstimate({
        pageId: roomToUpdate.dataForUI.metadata.pageId,
        estimateId: estimateId,
        metadata: updatedMetadata,
      });

      if (!response) {
        return false;
      }

      const modifiedEstimationDetail = structuredClone(currentEstimationDetail);
      modifiedEstimationDetail[roomIndexToUpdate].dataForUI.metadata = updatedMetadata;
      modifiedEstimationDetail[roomIndexToUpdate].dataForUI.roomName = updatedMetadata.roomName;

      dispatch(
        estimationActions.setEstimationState({
          estimationDetail: modifiedEstimationDetail,
        }),
      );
    },
    [dispatch, estimateId],
  );

  const assignClusterToRoom = useCallback(
    async (clusterId: string, room: EstimateDetail): Promise<boolean> => {
      const clusterToRoomMapping: ClusterToRoomMapping = {
        clusterId,
        roomId: room.dataForUI.metadata.pageId,
        isMappedByUser: true,
      };

      await updateEstimate({ clusterToRoomMappings: [clusterToRoomMapping] });

      toast.info(`Successfully merged with room "${room.dataForUI.metadata.roomName}"`);

      return true;
    },
    [updateEstimate],
  );

  const updateLineItems = useCallback(
    async (pageId: string, lineItems: MappedTuple[]): Promise<boolean> => {
      const startingState = store.getState().estimation.estimationDetail;
      if (!startingState) {
        console.error(`couldn't find estimate details when updating line items`);
        return false;
      }

      if (lineItems.some((lineItem) => lineItem.tuple.pageMetadata?.pageId !== lineItems[0].tuple.pageMetadata?.pageId)) {
        throw new Error(`Cannot update multiple line items from different rooms at the same time`);
      }

      const batch = buildLineItemUpdateBatch(startingState, pageId, lineItems);

      if (batch === null) {
        return false;
      }

      const response = await updateOrCreateLineItem({
        // this field isn't used by the backend, ok to leave blank
        lineItemId: "",
        estimateId: estimateId,
        pageId,
        lineItemData: batch.lineItemData,
        source: "",
        metadata: {},
      });

      if (response.success) {
        return true;
      } else {
        console.error(`failed to update some line items`);
        return false;
      }
    },
    [estimateId, updateOrCreateLineItem],
  );

  const updateLineItem = useCallback(
    async (
      pageId: string,
      tupleId: string,
      lineItemId: string,
      uniqueId: string,
      item?: string,
      quantity?: number,
      measurement?: string,
      formula?: IQuantityFormula,
      confirmed: boolean = true,
      newLineItemId?: string,
      orderIndex?: number,
      skipOrderIndexUpdate?: boolean,
      comment?: string,
    ) => {
      const latestState = store.getState().estimation.estimationDetail;
      if (!latestState) return;

      // 1. Apply update to state copy
      const { apiData } = applyUpdateToState(latestState, {
        pageId,
        tupleId,
        lineItemId,
        item,
        uniqueId,
        quantity,
        measurement,
        formula,
        confirmed,
        newLineItemId,
        orderIndex,
        skipOrderIndexUpdate,
        comment,
      });

      if (!apiData) {
        console.error("Failed to prepare state for update.");
        return;
      }

      // 2. Perform API Call
      try {
        const response = await updateOrCreateLineItem({
          lineItemId: apiData.tupleId,
          estimateId: estimateId,
          pageId: apiData.pageIdForApi,
          lineItemData: [apiData.tupleData],
          source: "",
          metadata: {},
        });

        if (!response.success) {
          toast.error("API Error: Could not update line item.");
          return;
        }

        // 4. Refetch the mapping under the new catalog id so the unit resolves for the renamed row.
        if (item !== undefined) {
          dispatch(estimationActions.updateXactimateMappingForLineItem(item, newLineItemId ?? lineItemId));
        }
      } catch (err) {
        console.error("Error updating line item API call:", err);
        toast.error("An unexpected error occurred during update.");
      }
    },
    [dispatch, estimateId, updateOrCreateLineItem],
  );

  const confirmExcluded = useCallback(
    async (pageId: string, tupleId: string, lineItemId: string) => {
      // The lookup state is only used for finding the tuple; we build the updated tuple immutably below.
      const state = store.getState().estimation.estimationDetail ?? [];
      const { currentPageIndex, tuples, tupleIndex } = getDetailFromPossiblyMergedPage(state, pageId, tupleId);

      if (state[currentPageIndex] && tuples[tupleIndex]) {
        // Find the specific line item
        const lineItemIndex = tuples[tupleIndex].lineItems.findIndex((item) => item.lineItemId === lineItemId);

        if (lineItemIndex === -1) return;

        const updatedTuple = {
          ...tuples[tupleIndex],
          lineItems: tuples[tupleIndex].lineItems.map((item) => {
            if (item.lineItemId === lineItemId) {
              return {
                ...item,
                // Reverse the reason they are excluded
                ...(item.isMutuallyExcluded && { isMutuallyExcluded: false }),
                ...(item.isExcludedBy && { isExcludedBy: [] }),
                ...(item.removedByTPARule && { removedByTPARule: false }),
                deleted: false,
                confidence: 1,
                confirmed: true,
              };
            }
            return item;
          }),
        };

        try {
          await updateOrCreateLineItem({
            lineItemId: tupleId,
            estimateId: estimateId,
            pageId,
            lineItemData: [updatedTuple] as unknown as LineItemData[],
            source: "",
            metadata: {},
          });
        } catch (err) {
          console.error("Error updating line item", err);
        }
      }
    },
    [estimateId, updateOrCreateLineItem],
  );

  const confirmRecommendation = useCallback(
    async (pageId: string, tupleId: string, lineItemId: string) => {
      const state = store.getState().estimation.estimationDetail ?? [];
      const { currentPageIndex, tuples, tupleIndex } = getDetailFromPossiblyMergedPage(state, pageId, tupleId);

      if (!state[currentPageIndex] || !tuples[tupleIndex]) {
        console.error(`can't find required state for confirmRecommendation`);
        return;
      }

      const sourceTuple = tuples[tupleIndex];
      const parentTupleId = sourceTuple.isComplementOf;
      const tupleParentFoundIndex = tuples.findIndex((tuple) => tuple.tupleId === parentTupleId);

      // If the parent line item is an issue, keep the confirmed complement in its own tuple instead of merging it into
      // the parent's tuple — that would pull it into the issue's tuple.
      const parentIsIssue = tupleParentFoundIndex !== -1 && !!tuples[tupleParentFoundIndex]?.hasError;

      // reset pageId to the current page (to account for merged pages)
      pageId = state[currentPageIndex].dataForUI.metadata.pageId;

      try {
        // tuple will not have a mergeable parent if it's in the General Room or its parent is an issue
        if (tupleParentFoundIndex === -1 || parentIsIssue) {
          const updatedTuple: Tuple = {
            ...sourceTuple,
            isLikely: false,
            lineItems: sourceTuple.lineItems.map((item) => (item.lineItemId === lineItemId ? { ...item, confidence: 1.0 } : item)),
          };

          await updateOrCreateLineItem({
            lineItemId: tupleId,
            estimateId: estimateId,
            pageId,
            lineItemData: [updatedTuple] as unknown as LineItemData[],
            source: "",
            metadata: {},
          });
        } else {
          const tupleParentFound = tuples[tupleParentFoundIndex];
          // Find the specific line item
          const lineItemToAdd = sourceTuple.lineItems.find((item) => item.lineItemId === lineItemId);

          if (!lineItemToAdd) {
            console.error("Line item recommendation not found");
            return;
          }

          const archivedSourceTuple: Tuple = { ...sourceTuple, archived: true };

          // Add the line item to the parent tuple
          const updatedTuple: Tuple = {
            ...tupleParentFound,
            lineItems: [
              ...tupleParentFound.lineItems,
              {
                ...lineItemToAdd,
                uniqueId: uuidv4(),
                confirmed: true,
                confidence: 1,
                deleted: false,
              },
            ],
          };

          const lineItemData = [archivedSourceTuple, updatedTuple] as unknown as LineItemData[];

          await updateOrCreateLineItem({
            lineItemId: tupleId,
            estimateId: estimateId,
            pageId,
            lineItemData,
            source: "",
            metadata: {},
          });
        }
        // Refetch authoritative data from the server
        queryClient.invalidateQueries({ queryKey: ESTIMATE_KEYS.detail(estimateId) });
      } catch (err) {
        toast.error("Error confirming recommendation. Contact support if this persists.");
        console.error("Error updating line item", err);
      }
    },
    [estimateId, updateOrCreateLineItem],
  );

  const confirmIssue = useCallback(
    async (pageId: string, tupleId: string, lineItemId: string) => {
      const state = store.getState().estimation.estimationDetail ?? [];
      const { currentPageIndex, tuples, tupleIndex } = getDetailFromPossiblyMergedPage(state, pageId, tupleId);

      const currentTuple = tuples[tupleIndex];
      const lineItemIndex = currentTuple?.lineItems.findIndex((item) => item.lineItemId === lineItemId);

      // reset pageId to the current page (to account for merged pages)
      pageId = state[currentPageIndex].dataForUI.metadata.pageId;

      // Get the line item that's being confirmed
      const lineItem = currentTuple?.lineItems[lineItemIndex];

      // Get the orderIndex from the line item if it exists
      const orderIndex = lineItem?.orderIndex;

      // An issue that's also a recommendation should become a regular complement once confirmed.
      const isRecommendation = !!currentTuple?.isLikely || !!lineItem?.isLikely;

      if (state[currentPageIndex] && currentTuple) {
        const updatedTuple = {
          ...currentTuple,
          hasError: false,
          hadErrorThatHasBeenFixed: true,
          ...(currentTuple.isFreeFormFlag && { isFreeFormFlag: false }),
          // A confirmed recommendation is no longer "likely" — it's now a regular complement.
          ...(isRecommendation && { isLikely: false }),
          // If the first line item is being confirmed and it has an orderIndex, apply it to the tuple
          ...(lineItemIndex === 0 && orderIndex !== undefined && { originalTupleIndex: orderIndex }),
          lineItems: tuples[tupleIndex].lineItems?.map((item: LineItem, index: number) => {
            const isConfirmedItem = index === lineItemIndex;
            return {
              ...item,
              confirmed: isConfirmedItem,
              confidence: isConfirmedItem ? 1 : item.confidence,
              // Confirming a recommendation converts it into a complement.
              ...(isConfirmedItem && isRecommendation ? { isLikely: false, isComplement: true } : {}),
            };
          }),
        };

        try {
          const updatedTuples: LineItemData[] = [updatedTuple];

          //old error resolution panel behavior
          if (lineItemIndex === 0 && currentTuple?.lineItems?.length > 1) {
            const recommendationTuples = tuples.filter((tuple: Tuple) => tuple.isComplementOf === currentTuple?.tupleId);

            for (const recommendationTuple of recommendationTuples) {
              const updatedRecommendationTuple = {
                ...recommendationTuple,
                hasError: false,
                isLikely: true,
                hadErrorThatHasBeenFixed: true,
                lineItems: recommendationTuple.lineItems?.map((item: LineItem) => {
                  return {
                    ...item,
                    isComplement: true,
                  };
                }),
              };

              updatedTuples.push(updatedRecommendationTuple);
            }
          }

          await updateOrCreateLineItem({
            lineItemId: tupleId,
            estimateId: estimateId,
            pageId,
            lineItemData: updatedTuples,
            source: "ErrorResolutionPanel",
            metadata: {},
          });

          unSelectLineItem();
        } catch (err) {
          console.error("Error updating line item", err);
        }
      }
    },
    [estimateId, updateOrCreateLineItem, unSelectLineItem],
  );

  const onConfirm = useCallback(
    async (pageId: string, tupleId: string, lineItemId: string, category?: Category) => {
      unSelectLineItem();

      switch (category) {
        case "issues":
          await confirmIssue(pageId, tupleId, lineItemId);
          return;
        case "recommended":
          await confirmRecommendation(pageId, tupleId, lineItemId);
          return;
        case "removeRules":
          await confirmExcluded(pageId, tupleId, lineItemId);
          return;

        default:
          return;
      }
    },
    [unSelectLineItem, confirmIssue, confirmRecommendation, confirmExcluded],
  );

  const updateOrCreateScopeNoteObject = useCallback(
    async (scopeNoteObject: AddNoteBody) => {
      dispatch(
        estimationActions.setEstimationState({
          isUpdateScopeNoteLoading: true,
        }),
      );

      try {
        const currentEstimation = store.getState().estimation.estimation;
        const _scopeNoteObjects = currentEstimation?.scopeNoteObjects ? [...currentEstimation.scopeNoteObjects] : [];
        const updateAtIndex = _scopeNoteObjects.findIndex(({ uuid }) => uuid === scopeNoteObject.uuid);
        if (updateAtIndex !== -1) {
          _scopeNoteObjects[updateAtIndex] = scopeNoteObject;
        } else {
          _scopeNoteObjects.push(scopeNoteObject);
        }

        await updateEstimate({ scopeNoteObjects: _scopeNoteObjects });
      } catch (err) {
        console.error("Error updating scope notes", err);
        toast.error("Error updating scope notes");
      }
      dispatch(
        estimationActions.setEstimationState({
          isUpdateScopeNoteLoading: false,
        }),
      );
    },
    [dispatch, updateEstimate],
  );

  const onSearchLineItem = useCallback(async (search: string, searchMode?: string): Promise<LineItemSearchData[]> => {
    try {
      const currentEstimation = store.getState().estimation.estimation;
      const estimateType = currentEstimation?.estimateType || EstimateType.RECONSTRUCTION;
      // Forward the estimate id so the server can restrict the search pool to the
      // rate card's items for T&M rate-card estimates. Without it the server falls
      // back to the global pool and returns non-rate-card line items.
      const response = await EstimateProvider.searchLineItems(search, estimateType, searchMode, currentEstimation?.estimateId);
      if (!response.success) return [];

      return response.data;
    } catch (error) {
      console.error("Error searching estimate line items", error);
      return [];
    }
  }, []);

  const getLineItemElements = useCallback(async (lineItemId: string): Promise<LineItemElementData[]> => {
    try {
      const response = await EstimateProvider.getLineItemElements(lineItemId);
      if (!response.success) return [];

      return response.data;
    } catch (error) {
      console.error("Error searching line item elements", error);
      return [];
    }
  }, []);

  // Fetch the unit(s) the Xactimate mapping allows for a (lineItem, lineItemId) pair, uppercased and
  // deduped. Used to reconcile a formula's unit at the moment a line item is re-mapped, before its
  // mapping has landed in Redux.
  const getAllowedUnitsForLineItem = useCallback(async (lineItem: string, lineItemId: string): Promise<string[]> => {
    try {
      const { data } = await EstimateProvider.getXactimateMappingForLineItem(lineItem, lineItemId);
      const units = (data ?? []).map((mapping) => mapping?.quantity?.trim().toUpperCase()).filter((unit): unit is string => !!unit);
      return Array.from(new Set(units));
    } catch (error) {
      console.error("Error fetching allowed units for line item", error);
      return [];
    }
  }, []);

  const updateMeasurement = useCallback(
    async (measurementData: MeasurementData[]) => {
      const currentEstimation = store.getState().estimation.estimation;
      const previousMeasurementData = currentEstimation?.measurementData;

      dispatch(
        estimationActions.setEstimationState({
          estimation: { ...currentEstimation, measurementData } as Estimation,
        }),
      );

      try {
        const response = await EstimateProvider.updateMeasurement({
          estimaticsData: {
            estimateId,
            measurementData,
          },
        });

        if (!response?.success) {
          dispatch(
            estimationActions.setEstimationState({
              estimation: { ...currentEstimation, measurementData: previousMeasurementData } as Estimation,
            }),
          );
          return false;
        }

        return true;
      } catch (err) {
        dispatch(
          estimationActions.setEstimationState({
            estimation: { ...currentEstimation, measurementData: previousMeasurementData } as Estimation,
          }),
        );
        console.error("Error updating estimate details", err);
        return false;
      }
    },
    [dispatch, estimateId],
  );
  // First, let's add a function to fetch xactimate mapping

  const addUserXactimateMapping = useCallback(async (mapping: IXactimateUserRowPayload) => {
    try {
      const response = await EstimateProvider.addUserXactimateMapping({
        mapping,
      });
      if (!response.success) {
        toast.error("Failed to save line item mapping. Please try again.", {
          className: "!text-red-500 !bg-red-500/10 !border-red-500",
        });
      }
      return response;
    } catch {
      toast.error("Error adding user xactimate mapping", {
        className: "!text-red-500 !bg-red-500/10 !border-red-500",
      });
      return { success: false, lineItem: null, mapping: null };
    }
  }, []);

  const onCancelMissingMapping = useCallback(() => {
    dispatch(
      estimationActions.setEstimationState({
        missingMappings: [],
        fixedMapping: [],
        showMissingMappingModal: false,
      }),
    );
  }, [dispatch]);

  const onCloseAlert = useCallback(() => {
    setShowAlert({ errors: [], isValid: true });
  }, []);

  const setCurrentViewEstimateId = useCallback(
    async (estimateId: string, _message?: string, isRefetch?: boolean) => {
      try {
        if (!estimateId) return;

        if (!isRefetch) {
          dispatch(
            estimationActions.setEstimationState({
              estimationCreated: undefined,
              isLoading: true,
            }),
          );
        }
        const response = await EstimateProvider.getEstimateDetail(estimateId);

        if (!response.success) {
          console.error("Error getting estimate detail");
          return;
        }
        const _estimates = response.data;

        if (!_estimates || _estimates.length === 0) {
          console.warn("No estimates found for estimateId:", estimateId);
          return;
        }

        const _documents = _estimates.map((estimate) => estimate.dataForUI);

        if (_documents.length && _documents[0]?.metadata?.pageIndex) {
          _documents.sort((a, b) => (a.metadata.pageIndex ?? 0) - (b.metadata.pageIndex ?? 0));
        }

        const allPages: {
          page: Page;
          documentIndex: number;
          document: DataForUI;
        }[] = [];

        let documentIndex = 0;
        for (const document of _documents) {
          const pageInfo = {
            page: document.page,
            documentIndex,
            document,
          };

          allPages.push(pageInfo);

          documentIndex++;
        }

        allPages.forEach((page) => {
          const tuplesOutput = page.page.tuplesOutput
            .filter((tuple) => !isItemArchived(tuple))
            .map((tuple, index) => ({
              ...tuple,
              originalTupleIndex:
                tuple.lineItems[0]?.orderIndex !== undefined
                  ? tuple.lineItems[0].orderIndex
                  : index +
                    EngineSharedUtils.prefixOrder.findIndex((prefix) => tuple.tupleId.replace(/^tpa_/, "").startsWith(prefix)) * 1000, // keep this for backwards compatibility for the moment
            }));

          page.page.tuplesOutput = tuplesOutput;
        });
        dispatch(
          estimationActions.setEstimationState({
            currentViewEstimateId: estimateId,
            allCurrentViewPages: allPages,
            currentViewEstimates: _estimates,
            isLoading: false,
          }),
        );
      } catch (error) {
        dispatch(
          estimationActions.setEstimationState({
            isLoading: false,
          }),
        );
        console.error("Error fetching estimate data:", error);
      }
    },
    [dispatch],
  );

  // Tracks the estimate we already refetched for once its processing completed, so that a completion
  // event triggers a single refetch instead of an infinite loop. Reset when a new processing cycle
  // begins (completion condition drops) so a subsequent completion refetches again.
  const refetchedForCompletedEstimateRef = useRef<string | null>(null);

  // UseEffect to check if processing has completed - triggers a refetch of estimate detail
  useEffect(() => {
    // Page-level concern: only the estimation-detail page instance (pollJobs: true) runs this.
    // ~15 components mount this hook on the detail page, each with its own guard ref — without
    // this gate every instance fires its own completion refetch, and each refetch re-renders the
    // others into re-running this effect.
    if (!options.pollJobs) return;
    // The estimate was deleted — stop refetching so we don't tight-loop on a missing estimate.
    if (estimateNotFound) return;
    // Don't trigger refetch if we already have data loaded or if a fetch is in progress
    const hasItems = estimationDetail?.some((item) => item.dataForUI?.page.tuplesOutput?.length > 0) ?? false;
    if (hasItems || isLoading) return;

    const refetchOnce = () => {
      // Guard against re-firing for the same completion: an estimate with zero line items never sets
      // `hasItems`, so without this we would refetch `get_final_estimates` on every re-render forever.
      if (refetchedForCompletedEstimateRef.current === estimateId) return;
      refetchedForCompletedEstimateRef.current = estimateId;
      clearRequestsCache();
      queryClient.invalidateQueries({ queryKey: ESTIMATE_KEYS.detail(estimateId) });
      queryClient.invalidateQueries({ queryKey: ESTIMATE_KEYS.xactimateMapping(estimateId) });
      setShouldFetchEstimate(true);
      searchEstimateDetail(estimateId);
    };

    // Refetch once the polled jobs have settled. A failure counts as settled: nothing more is coming,
    // and the estimate may still hold partial results worth loading — waiting on `allCompleted`, which
    // a failed job can never satisfy, would leave the page empty indefinitely. `hasFailedJobs` covers
    // the estimate's whole job history and so never goes back to false, hence the `hasActiveJobs` check:
    // it is what lets a retry after a failure read as a new cycle and clear the guard ref below.
    if (allCompleted || (hasFailedJobs && !hasActiveJobs)) {
      refetchOnce();
    } else if (refetchedForCompletedEstimateRef.current === estimateId) {
      // New processing cycle (jobs active again) — allow the next completion to refetch.
      refetchedForCompletedEstimateRef.current = null;
    }
  }, [
    estimateId,
    setCurrentViewEstimateId,
    estimationDetail,
    isLoading,
    allCompleted,
    hasFailedJobs,
    hasActiveJobs,
    searchEstimateDetail,
    estimateNotFound,
    options.pollJobs,
  ]);

  // Add/update-documentation reprocessing is tracked by the estimate's scopeNoteProcessingStatus,
  // not by BullMQ jobs at submit time (the job is enqueued asynchronously after the request
  // returns). While it is "processing", poll the estimate metadata so we observe the flip to
  // "complete"/"error"; on that flip, refetch the estimate detail so the newly-generated line
  // items load. Page-level only (pollJobs) so the ~15 hook instances on the detail page don't each
  // spin up their own poll.
  const scopeNoteProcessingStatus = estimation?.scopeNoteProcessingStatus;
  const prevScopeNoteProcessingStatusRef = useRef<ScopeNoteProcessingStatus | undefined>(scopeNoteProcessingStatus);
  useEffect(() => {
    if (!options.pollJobs) return;
    if (estimateNotFound) return;

    const previousStatus = prevScopeNoteProcessingStatusRef.current;
    prevScopeNoteProcessingStatusRef.current = scopeNoteProcessingStatus;

    // Reprocessing just finished — pull the freshly generated line items.
    if (previousStatus === "processing" && (scopeNoteProcessingStatus === "complete" || scopeNoteProcessingStatus === "error")) {
      clearRequestsCache();
      queryClient.invalidateQueries({ queryKey: ESTIMATE_KEYS.detail(estimateId) });
      queryClient.invalidateQueries({ queryKey: ESTIMATE_KEYS.xactimateMapping(estimateId) });
      searchEstimateDetail(estimateId);
      return;
    }

    // Still reprocessing — keep refetching metadata until the status flips.
    if (scopeNoteProcessingStatus === "processing") {
      const timer = setInterval(() => setShouldFetchEstimate(true), SCOPE_NOTE_REPROCESS_POLL_MS);
      return () => clearInterval(timer);
    }
  }, [options.pollJobs, estimateNotFound, scopeNoteProcessingStatus, estimateId, searchEstimateDetail]);

  const enhancedRoomsCacheRef = useRef<Map<string, EnhancedRoomCacheEntry>>(new Map());

  const rooms = useMemo(() => {
    if (!estimationDetail) return [];

    // Per-pageId cache: when a source room reference is unchanged across renders (and its
    // merge inputs unchanged), reuse the previous enhanced room reference so React.memo on Room
    // can bail out. structuredClone'ing every room on every render destroyed this stability.
    const prevCache = enhancedRoomsCacheRef.current;
    const newCache = new Map<string, EnhancedRoomCacheEntry>();

    // Process rooms, handling mergeWith relationships
    const processedRooms = estimationDetail
      .filter((room) => {
        // Skip archived rooms and rooms that should be merged into another room
        return !isItemArchived(room.dataForUI.metadata) && !room.dataForUI.page.mergeWith;
      })
      .map((room) => {
        const pageId = room.dataForUI.metadata.pageId;
        // Find all rooms that should be merged into this one
        const mergedRooms = estimationDetail.filter((otherRoom) => otherRoom.dataForUI.page.mergeWith === pageId);

        const cached = prevCache.get(pageId);
        const cacheHit =
          cached !== undefined &&
          cached.source === room &&
          cached.mergedSources.length === mergedRooms.length &&
          mergedRooms.every((mr, i) => cached.mergedSources[i] === mr);

        if (cacheHit) {
          newCache.set(pageId, cached);
          return cached.output;
        }

        // Combine line items from all merged rooms
        let allTuples: Tuple[] = [...room.dataForUI.page.tuplesOutput];
        if (mergedRooms.length > 0) {
          mergedRooms.forEach((mergedRoom) => {
            if (mergedRoom.dataForUI.page.tuplesOutput?.length) {
              allTuples = [...allTuples, ...mergedRoom.dataForUI.page.tuplesOutput];
            }
          });
        }

        // Sort the combined tuples by originalTupleIndex
        allTuples = allTuples.map((tuple, index) => ({
          ...tuple,
          originalTupleIndex:
            tuple.lineItems[0]?.orderIndex !== undefined
              ? tuple.lineItems[0].orderIndex
              : index + EngineSharedUtils.prefixOrder.findIndex((prefix) => tuple.tupleId.replace(/^tpa_/, "").startsWith(prefix)) * 1000, // keep this for backwards compatibility for the moment
        }));

        const output: EstimateDetail = {
          ...room,
          dataForUI: {
            ...room.dataForUI,
            page: {
              ...room.dataForUI.page,
              tuplesOutput: allTuples,
            },
          },
          photos: [],
          clusterIds: [],
        };
        newCache.set(pageId, { source: room, mergedSources: mergedRooms, output });
        return output;
      });

    enhancedRoomsCacheRef.current = newCache;

    const photos = (files || [])?.filter((file) => !!file.uploadId && file.fileType === "image");
    const processedRoomsById = processedRooms.reduce(
      (acc, room) => {
        acc[room.dataForUI.metadata.pageId] = room;
        return acc;
      },
      {} as Record<string, EstimateDetail>,
    );

    // Cached rooms keep their existing clusterIds/photos array references — clear in place so the
    // photo loop below repopulates them fresh without breaking room-level referential equality.
    for (const room of processedRooms) {
      if (room.clusterIds) room.clusterIds.length = 0;
      if (room.photos) room.photos.length = 0;
    }

    for (const photo of photos) {
      const clusterId = photo.metadata?.clusterId ?? "";
      const roomId = photo.metadata?.assignedRoomId ?? clusterId;

      if (!processedRoomsById[roomId]) {
        const roomName =
          photo.metadata?.assignedRoomId && photo.metadata?.assignedRoomId in processedRoomsById
            ? processedRoomsById[photo.metadata?.assignedRoomId].dataForUI.metadata.roomName
            : photo.metadata?.roomName || "";

        const room: EstimateDetail = {
          dataForUI: {
            itemForDebugOnly: null,
            metadata: {
              fullPageImagePath: "",
              originalName: roomName,
              sourceImageName: "",
              uploadId: "",
              pageId: roomId,
              pageName: "",
              roomName: roomName,
              pageIndex: -1,
            },
            page: { fullPageImagePath: "", tuplesOutput: [] },
            originalName: roomName,
            roomName,
          },
          isUnmatchedCluster: true,
          photos: [],
          clusterIds: [],
        };

        processedRoomsById[roomId] = room;
      }

      if (!processedRoomsById[roomId].clusterIds?.includes(clusterId)) {
        processedRoomsById[roomId].clusterIds?.push(clusterId);
      }
    }

    // Sort the processed rooms by their pageIndex
    return Object.values(processedRoomsById).sort((a, b) => {
      if (a.isUnmatchedCluster) {
        return 1;
      }
      if (b.isUnmatchedCluster) {
        return -1;
      }

      const pageIndexA = a.dataForUI.metadata.pageIndex ?? 0;
      const pageIndexB = b.dataForUI.metadata.pageIndex ?? 0;
      return pageIndexA - pageIndexB;
    });
  }, [estimationDetail]);

  const roomTabOptions = useMemo(() => {
    if (!rooms) return [];

    // Unmatched photo clusters are no longer surfaced as rooms/tabs in the estimate view.
    return rooms
      .filter((room) => !room.isUnmatchedCluster)
      .map((room) => {
        return {
          value: capitalizeWords(room.dataForUI?.roomName),
          id: room.dataForUI?.metadata.pageId,
          pageIndex: room.dataForUI?.metadata.pageIndex ?? 0,
          fileName: room.dataForUI?.metadata.sourceImageName,
        };
      });
  }, [rooms]);

  const deleteThenCreateLineItem = useCallback(
    async (deleteParams: DeleteLineItemParams, createParams: CreateLineItemParams) => {
      const latestState = store.getState().estimation.estimationDetail;
      if (!latestState) return;

      // 1. Apply delete to state copy
      const deleteResult = _applyDeleteToState(latestState, deleteParams);
      if (!deleteResult.modifiedState || !deleteResult.apiData) {
        console.error("Failed to prepare state for deletion (in deleteThenCreate)."); // Keep console error
        return;
      }
      const stateAfterDelete = deleteResult.modifiedState; // Intermediate state
      const deleteApiData = deleteResult.apiData;

      // 2. Apply create to the *intermediate* state copy
      const createResult = _applyCreateToState(stateAfterDelete, createParams);
      if (!createResult.apiData) {
        console.error("Failed to prepare state for creation (in deleteThenCreate)."); // Keep console error
        return;
      }
      const createApiData = createResult.apiData;

      // 3. Perform API Calls (Consider parallel execution if safe)
      try {
        const response = await updateOrCreateLineItem({
          lineItemId: deleteApiData.tupleId,
          estimateId: estimateId,
          pageId: deleteApiData.pageIdForApi,
          lineItemData: [deleteApiData.tupleData, createApiData.lineItemData], // Send the data *as it was* when deleted
          source: "",
          metadata: {},
        });

        if (!response.success) {
          console.error("API Error: deleteThenCreate operation failed.");
          // Handle potential partial success/failure if necessary
          return;
        }

        unSelectLineItem();
      } catch (err) {
        console.error("Error in deleteThenCreateLineItem API calls:", err);
        // toast.error('An unexpected error occurred during delete/create.'); // Kept this one for overall failure
      }
    },
    [estimateId, updateOrCreateLineItem, unSelectLineItem],
  );

  const deleteThenUpdateLineItem = useCallback(
    async (deleteParams: DeleteLineItemParams, updateParams: UpdateLineItemParams) => {
      // Get the latest state from the store
      const latestState = store.getState().estimation.estimationDetail;
      if (!latestState) return;

      // 1. Apply delete
      const deleteResult = _applyDeleteToState(latestState, deleteParams);
      if (!deleteResult.modifiedState || !deleteResult.apiData) {
        // toast.error('Failed to prepare state for deletion (in deleteThenUpdate).'); // Removed this line
        console.error("Failed to prepare state for deletion (in deleteThenUpdate)."); // Keep console error
        return;
      }
      const stateAfterDelete = deleteResult.modifiedState;
      const deleteApiData = deleteResult.apiData;

      // 2. Apply update to intermediate state
      const updateResult = applyUpdateToState(stateAfterDelete, updateParams);
      if (!updateResult.modifiedState || !updateResult.apiData) {
        // toast.error('Failed to prepare state for update (in deleteThenUpdate).'); // Removed this line
        console.error("Failed to prepare state for update (in deleteThenUpdate)."); // Keep console error
        return;
      }
      const updateApiData = updateResult.apiData;

      // 3. API Calls
      try {
        const response = await updateOrCreateLineItem({
          lineItemId: deleteApiData.tupleId,
          estimateId: estimateId,
          pageId: deleteApiData.pageIdForApi,
          lineItemData: [deleteApiData.tupleData, updateApiData.tupleData], // Send tuple state *before* update
          source: "",
          metadata: {},
        });
        if (!response.success) {
          toast.error("Error updating line item");
          return;
        }

        unSelectLineItem();
      } catch (err) {
        console.error("Error in deleteThenUpdateLineItem API Calls:", err);
        toast.error("An unexpected error occurred while updating line item."); // Kept this one
      }
    },
    [estimateId, updateOrCreateLineItem, unSelectLineItem],
  );

  const [showTPARulesModal, setShowTPARulesModal] = useState(false);

  const refetchEstimation = useCallback(() => setShouldFetchEstimate(true), []);

  return {
    searchEstimateDetail,
    alert: showAlert,
    estimationDetail,
    lineItemXactimateMapping,
    rateCardUnitByTemplateId,
    rightSidebarCurrentTab,
    estimation,
    isPdfLoading,
    currentRoom,
    lineItemDetail,
    selectLineItem,
    unSelectLineItem,
    isLoading,
    isUpdateScopeNoteLoading,
    isUpdateLineItemLoading,
    fullPageImagePath,
    updateOrCreateScopeNoteObject,
    updateMetadataForExpectedDoc,
    notes,
    links,
    files,
    onSearchLineItem,
    onConfirm,
    updateMeasurement,
    updateLineItems,
    fixedMapping,
    missingMappings,
    showMissingMappingModal,
    onCancelMissingMapping,
    onCloseAlert,
    resetEstimationDetailData,
    rooms,
    getLineItemElements,
    getAllowedUnitsForLineItem,
    roomTabOptions,
    addUserXactimateMapping,
    deleteLineItem,
    createLineItem,
    updateLineItem,
    deleteThenCreateLineItem,
    deleteThenUpdateLineItem,
    exportedToXactimate,
    hasPdfExport,
    showTPARulesModal,
    setShowTPARulesModal,
    assignClusterToRoom,
    knownLineItemKeys: knownLineItemComponentKeys,
    addKnownLineItemKey,
    refetchEstimation,
    estimateNotFound,
    updateEstimate,
  };
};
